export function resetLocalStorageAfterAttendance() {
    const auth = localStorage.getItem('auth');
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const theme = localStorage.getItem('theme') || 'light';

    // 1. Clear ALL storage
    localStorage.clear();
    localStorage.removeItem('attendanceStatus'); // Ensure this key is removed

    // 2. Restore/Initialize exactly 10 keys from login state
    const cleanLoginState = {
        auth,
        isLoggedIn: isLoggedIn || 'true',
        theme,
        dailyTask: null,
        runningTasks: JSON.stringify([]),
        checkedTasks: JSON.stringify([]),
        shift_overtime_history: JSON.stringify([]),
        shift_overtime_remarks_history: JSON.stringify([]),
        taskButtonStates: JSON.stringify([]),
        tomorrowTask: JSON.stringify([])
    };

    Object.entries(cleanLoginState).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            localStorage.setItem(key, value);
        } else if (value === null) {
            // Explicitly set some to null if they should be null (like dailyTask)
            localStorage.setItem(key, 'null');
        }
    });

    // Reset dailyTask specifically
    localStorage.setItem('dailyTask', 'null');
}

/**
 * Generates and downloads a CSV file with attendance data for manual recovery.
 * Format matches the system's 15-column attendance import requirements.
 */
export function downloadRescueCSV(payload, userName, userProfile) {
    try {
        const date = new Date().toLocaleDateString('en-US'); // MM/DD/YYYY
        const timeIn = payload.timeIn ? new Date(payload.timeIn).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : "";
        const timeOut = payload.timeOut ? new Date(payload.timeOut).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : "";

        // Status Calculation (Dual Logic: Late takes priority)
        let status = payload.status || "Present";

        // 1. Check Late (if not already set or override)
        if (userProfile?.workSchedule) {
            const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
            const currentDay = days[new Date().getDay()];
            const schedule = userProfile.workSchedule[currentDay];

            if (schedule && schedule.lateTime && payload.timeIn) {
                const [lateH, lateM] = schedule.lateTime.split(':').map(Number);
                const arrival = new Date(payload.timeIn);
                const lateThreshold = new Date(arrival);
                lateThreshold.setHours(lateH, lateM, 0, 0);

                if (arrival > lateThreshold) {
                    status = "Late";
                }
            }
        }

        // 2. Check Early Clock Out (If not Late)
        if (status !== "Late" && payload.productiveHours) {
            const prodStr = payload.productiveHours;
            const hMatch = prodStr.match(/(\d+)h/);
            const mMatch = prodStr.match(/(\d+)m/);
            const h = hMatch ? parseInt(hMatch[1]) : 0;
            const m = mMatch ? parseInt(mMatch[1]) : 0;
            const totalMinutes = (h * 60) + m;
            if (totalMinutes < 465) { // 7h 45m = 465 mins
                status = "Early Clock Out";
            }
        }

        const rows = [
            [
                "Employee Code", "Name ", "Date", "Check-In", "Check-Out",
                "Status", "Notes", "Task Description", "Task Duration",
                "Productive Hours", "Shift Hours", "Break Hours",
                "Points", "Grade Points", "Week"
            ],
            [
                payload.employeeCode || payload.identifier || userProfile?.employeeId || "",
                userName || userProfile?.fullName || "Unknown",
                date,
                timeIn,
                timeOut,
                status,
                payload.notes || "",
                JSON.stringify(payload.dailyTask || []).replace(/"/g, '""'), // Escape quotes for CSV
                payload.productiveHours || "", // Using productive hours as duration placeholder
                payload.productiveHours || "0h 0m",
                userProfile?.shiftHours || "9",
                payload.breakHours || "0h 0m",
                payload.points || "0",
                payload.gradePoints || "F",
                getWeekNumber(new Date())
            ]
        ];

        const csvContent = rows.map(r => r.map(cell => `"${cell}"`).join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `rescue_${userProfile?.fullName || 'attendance'}_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return true;
    } catch (error) {
        console.error("Failed to generate rescue CSV:", error);
        return false;
    }
}

export function downloadBulkRescueCSV(logs) {
    try {
        const header = [
            "Employee Code", "Name ", "Date", "Check-In", "Check-Out",
            "Status", "Notes", "Task Description", "Task Duration",
            "Productive Hours", "Shift Hours", "Break Hours",
            "Points", "Grade Points", "Week"
        ];

        const rows = logs.map(log => {
            const payload = log.attendanceData || {};
            const metadata = log.metadata || {};
            const logDate = new Date(log.attemptedAt || new Date());
            const dateStr = logDate.toLocaleDateString('en-US');

            const timeIn = payload.timeIn ? new Date(payload.timeIn).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : "";
            const timeOut = payload.timeOut ? new Date(payload.timeOut).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : "";

            return [
                log.employeeCode || payload.employeeCode || metadata.employeeCode || payload.identifier || log.userEmail || "",
                log.userName || "Unknown",
                dateStr,
                timeIn,
                timeOut,
                payload.status || "Present",
                payload.notes || "",
                JSON.stringify(payload.dailyTask || []).replace(/"/g, '""'),
                payload.productiveHours || "",
                payload.productiveHours || "0h 0m",
                "9", // Default shift hours
                payload.breakHours || "0h 0m",
                payload.points || "0",
                payload.gradePoints || "F",
                getWeekNumber(logDate)
            ];
        });

        const csvContent = [header, ...rows].map(r => r.map(cell => `"${cell}"`).join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `bulk_recovery_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return true;
    } catch (error) {
        console.error("Failed to generate bulk rescue CSV:", error);
        return false;
    }
}

function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}